import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Search, Bell, BellRing, User, X, Check, Send, Sparkles, AlertCircle } from 'lucide-react';
import { useLang } from '@/lib/i18n';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

const NAV_TEXTS = {
  pl: {
    home: 'Główna',
    models: 'Modelki',
    search: 'Szukaj',
    push: 'Powiadomienia',
    cabinet: 'Konto',
    login: 'Logowanie',
    pushTitle: 'Powiadomienia Push',
    pushDesc: 'Bądź na bieżąco z nowymi zweryfikowanymi ankietami modelek w Twoim mieście.',
    pushActive: 'Powiadomienia są włączone',
    pushInactive: 'Powiadomienia są wyłączone',
    enablePush: 'Włącz powiadomienia',
    disablePush: 'Wyłącz powiadomienia',
    sendTest: 'Wyślij testowe powiadomienie',
    testSent: 'Testowe powiadomienie wysłane!',
    testFailed: 'Nie udało się wysłać testu',
    enabledSuccess: 'Powiadomienia zostały włączone!',
    disabledSuccess: 'Powiadomienia wyłączone.',
    permissionDenied: 'Powiadomienia zostały zablokowane w ustawieniach przeglądarki.',
    unsupported: 'Przeglądarka nie obsługuje powiadomień Push.',
    iosPwaNotice: 'Na urządzeniach iOS (iPhone/iPad) powiadomienia Push działają po dodaniu aplikacji do ekranu głównego (PWA).',
    installPwaBtn: 'Zainstaluj PWA na iPhone',
    feature1: 'Nowe ankiety i zdjęcia w Twoim mieście',
    feature2: 'Oferty specjalne i zniżki VIP',
    feature3: '100% dyskrecji — powiadomienia można wyłączyć w każdej chwili'
  },
  ru: {
    home: 'Главная',
    models: 'Модели',
    search: 'Поиск',
    push: 'Уведомления',
    cabinet: 'Кабинет',
    login: 'Вход',
    pushTitle: 'Push-уведомления',
    pushDesc: 'Получайте мгновенные уведомления о новых анкетах моделей в вашем городе.',
    pushActive: 'Уведомления включены',
    pushInactive: 'Уведомления выключены',
    enablePush: 'Включить уведомления',
    disablePush: 'Выключить уведомления',
    sendTest: 'Отправить тестовый пуш',
    testSent: 'Тестовое уведомление отправлено!',
    testFailed: 'Не удалось отправить тест',
    enabledSuccess: 'Push-уведомления успешно включены!',
    disabledSuccess: 'Push-уведомления отключены.',
    permissionDenied: 'Уведомления заблокированы в настройках браузера.',
    unsupported: 'Ваш браузер не поддерживает Push-уведомления.',
    iosPwaNotice: 'На iPhone / iPad уведомления работают, когда сайт добавлен на экран «Домой» (PWA).',
    installPwaBtn: 'Установить PWA на iPhone',
    feature1: 'Новые модели и свежие фото в вашем городе',
    feature2: 'Специальные акции и VIP-предложения',
    feature3: '100% приватность — отписка в 1 клик в любое время'
  },
  en: {
    home: 'Home',
    models: 'Models',
    search: 'Search',
    push: 'Alerts',
    cabinet: 'Account',
    login: 'Login',
    pushTitle: 'Push Notifications',
    pushDesc: 'Get instant alerts about new verified profiles in your area.',
    pushActive: 'Notifications are active',
    pushInactive: 'Notifications are disabled',
    enablePush: 'Enable Notifications',
    disablePush: 'Disable Notifications',
    sendTest: 'Send test notification',
    testSent: 'Test notification sent!',
    testFailed: 'Failed to send test alert',
    enabledSuccess: 'Push notifications enabled!',
    disabledSuccess: 'Push notifications disabled.',
    permissionDenied: 'Notifications blocked in browser settings.',
    unsupported: 'Push notifications not supported in this browser.',
    iosPwaNotice: 'On iOS devices, Push notifications require installing the app to Home Screen (PWA).',
    installPwaBtn: 'Install PWA on iPhone',
    feature1: 'New profiles in your city',
    feature2: 'Special VIP offers and updates',
    feature3: '100% discreet — toggle off anytime'
  },
  ua: {
    home: 'Головна',
    models: 'Моделі',
    search: 'Пошук',
    push: 'Сповіщення',
    cabinet: 'Кабінет',
    login: 'Вхід',
    pushTitle: 'Push-сповіщення',
    pushDesc: 'Отримуйте миттєві сповіщення про нові анкети моделей у вашому місті.',
    pushActive: 'Сповіщення увімкнені',
    pushInactive: 'Сповіщення вимкнені',
    enablePush: 'Увімкнути сповіщення',
    disablePush: 'Вимкнути сповіщення',
    sendTest: 'Надіслати тестове сповіщення',
    testSent: 'Тестове сповіщення надіслано!',
    testFailed: 'Не вдалося надіслати тест',
    enabledSuccess: 'Push-сповіщення успішно увімкнені!',
    disabledSuccess: 'Push-сповіщення вимкнені.',
    permissionDenied: 'Сповіщення заблоковані у налаштуваннях браузера.',
    unsupported: 'Ваш браузер не підтримує Push-сповіщення.',
    iosPwaNotice: 'На iPhone / iPad сповіщення працюють після додавання додатка на головний екран (PWA).',
    installPwaBtn: 'Встановити PWA на iPhone',
    feature1: 'Нові анкети та фото у вашому місті',
    feature2: 'Спеціальні пропозиції та VIP-оновлення',
    feature3: '100% приватність — відписка в 1 клік у будь-який час'
  }
};

export default function MobileBottomNav() {
  const location = useLocation();
  const { lang = 'pl' } = useLang() || {};
  const t = NAV_TEXTS[lang] || NAV_TEXTS.pl;

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user_data') || 'null');
    } catch {
      return null;
    }
  });

  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState('');
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Sync user state on navigation
  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem('user_data') || 'null'));
    } catch {
      setUser(null);
    }
  }, [location.pathname]);

  // Check push subscription & device type
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent.toLowerCase();
    setIsIos(/iphone|ipad|ipod/.test(ua) && !window.MSStream);
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        if (sub) {
          setSubscribed(true);
          setCurrentEndpoint(sub.endpoint);
        } else {
          setSubscribed(false);
        }
      });
    }).catch(() => {});
  }, []);

  const enablePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
      toast.error(t.unsupported);
      return;
    }
    setLoading(true);
    try {
      let reg = await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error(t.permissionDenied);
        setLoading(false);
        return;
      }

      const keyRes = await fetch(`${API}/api/push/public-key`);
      const keyData = await keyRes.json();
      if (!keyData.publicKey) throw new Error('No public VAPID key');

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(keyData.publicKey),
        });
      }

      const subJson = sub.toJSON();
      const token = localStorage.getItem('user_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API}/api/push/subscribe`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        }),
      });

      setSubscribed(true);
      setCurrentEndpoint(sub.endpoint);
      toast.success(t.enabledSuccess);
    } catch (err) {
      console.error('[Push enable error]', err);
      toast.error(t.unsupported);
    } finally {
      setLoading(false);
    }
  };

  const disablePush = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const endpoint = sub ? sub.endpoint : '';
      if (sub) await sub.unsubscribe();

      const token = localStorage.getItem('user_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ endpoint }),
      });

      setSubscribed(false);
      setCurrentEndpoint('');
      toast.success(t.disabledSuccess);
    } catch (err) {
      console.error('[Push disable error]', err);
    } finally {
      setLoading(false);
    }
  };

  const sendTestPush = async () => {
    setTestSending(true);
    try {
      const res = await fetch(`${API}/api/push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: currentEndpoint }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t.testSent);
      } else {
        toast.error(data.error || t.testFailed);
      }
    } catch (err) {
      toast.error(t.testFailed);
    } finally {
      setTestSending(false);
    }
  };

  const pathname = location.pathname;
  const isHome = pathname === '/';
  const isModels = pathname.startsWith('/modelki') || pathname.startsWith('/miasta');
  const isSearch = pathname === '/szukaj' || pathname === '/szukaj-po-parametrach';
  const isCabinet = pathname.startsWith('/konto') || pathname === '/logowanie' || pathname === '/rejestracja';

  return (
    <>
      {/* Mobile Bottom Navigation Bar */}
      <nav
        aria-label="Nawigacja mobilna"
        className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[#0c0c0e]/95 backdrop-blur-2xl border-t border-white/10 shadow-[0_-10px_35px_rgba(0,0,0,0.85)] pb-[calc(env(safe-area-inset-bottom,0px)+6px)] pt-1.5 px-2 text-white"
      >
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {/* 1. Home */}
          <Link
            to="/"
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all duration-200 ${
              isHome ? 'text-rose-500 scale-105' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <div className={`p-1 rounded-xl transition-colors ${isHome ? 'bg-rose-500/15' : ''}`}>
              <Home className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium tracking-tight mt-0.5">{t.home}</span>
          </Link>

          {/* 2. Models */}
          <Link
            to="/modelki"
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all duration-200 ${
              isModels ? 'text-rose-500 scale-105' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <div className={`p-1 rounded-xl transition-colors ${isModels ? 'bg-rose-500/15' : ''}`}>
              <Users className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium tracking-tight mt-0.5">{t.models}</span>
          </Link>

          {/* 3. Search */}
          <Link
            to="/szukaj"
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all duration-200 ${
              isSearch ? 'text-rose-500 scale-105' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <div className={`p-1 rounded-xl transition-colors ${isSearch ? 'bg-rose-500/15' : ''}`}>
              <Search className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium tracking-tight mt-0.5">{t.search}</span>
          </Link>

          {/* 4. Push Alerts */}
          <button
            type="button"
            onClick={() => setPushModalOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all duration-200 relative ${
              pushModalOpen ? 'text-rose-500 scale-105' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <div className="relative p-1 rounded-xl">
              {subscribed ? (
                <BellRing className="w-5 h-5 text-rose-500" />
              ) : (
                <Bell className="w-5 h-5" />
              )}
              {/* Status indicator dot */}
              <span
                className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full border border-[#0c0c0e] ${
                  subscribed ? 'bg-emerald-400' : 'bg-rose-500 animate-pulse'
                }`}
              />
            </div>
            <span className="text-[10px] font-medium tracking-tight mt-0.5">{t.push}</span>
          </button>

          {/* 5. Cabinet / Login */}
          <Link
            to={user ? '/konto' : '/logowanie'}
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all duration-200 ${
              isCabinet ? 'text-rose-500 scale-105' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <div className={`p-1 rounded-xl transition-colors ${isCabinet ? 'bg-rose-500/15' : ''}`}>
              <User className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium tracking-tight mt-0.5 truncate max-w-[58px]">
              {user ? user.username || t.cabinet : t.login}
            </span>
          </Link>
        </div>
      </nav>

      {/* Push Notification Control Modal */}
      {pushModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200"
        >
          <div className="relative w-full max-w-md rounded-3xl bg-[#111115] border border-white/15 p-5 sm:p-6 shadow-2xl text-white animate-in slide-in-from-bottom duration-300">
            {/* Modal header */}
            <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-rose-500/15 border border-rose-500/20 text-rose-400 flex items-center justify-center">
                  <BellRing className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-base font-bold">{t.pushTitle}</h4>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`w-2 h-2 rounded-full ${subscribed ? 'bg-emerald-400' : 'bg-white/30'}`} />
                    <span className="text-xs text-white/60">
                      {subscribed ? t.pushActive : t.pushInactive}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPushModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* iOS Notice if not installed in standalone */}
            {isIos && !isStandalone && (
              <div className="mb-4 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-2.5 text-xs text-amber-200/90 leading-relaxed">
                <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="mb-2">{t.iosPwaNotice}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setPushModalOpen(false);
                      window.dispatchEvent(new CustomEvent('open-pwa-install'));
                    }}
                    className="underline font-semibold text-white hover:text-amber-300"
                  >
                    {t.installPwaBtn} →
                  </button>
                </div>
              </div>
            )}

            <p className="text-xs text-white/70 mb-4 leading-relaxed">
              {t.pushDesc}
            </p>

            {/* Benefit bullets */}
            <div className="space-y-2 mb-5 text-xs text-white/80">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5" />
                </span>
                <span>{t.feature1}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5" />
                </span>
                <span>{t.feature2}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5" />
                </span>
                <span>{t.feature3}</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2.5">
              {subscribed ? (
                <>
                  <button
                    type="button"
                    onClick={sendTestPush}
                    disabled={testSending}
                    className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white font-semibold text-xs sm:text-sm shadow-lg shadow-rose-950/40 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    <span>{testSending ? 'Wysyłanie...' : t.sendTest}</span>
                  </button>
                  <button
                    type="button"
                    onClick={disablePush}
                    disabled={loading}
                    className="w-full py-2.5 px-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-xs font-medium transition-colors"
                  >
                    {t.disablePush}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={enablePush}
                  disabled={loading}
                  className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-rose-600 via-pink-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white font-semibold text-xs sm:text-sm shadow-lg shadow-rose-950/40 flex items-center justify-center gap-2 transition-all disabled:opacity-50 active:scale-[0.98]"
                >
                  <BellRing className="w-4 h-4" />
                  <span>{loading ? 'Aktywacja...' : t.enablePush}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
